import React from 'react';
import { ChevronDown, Truck, Search, CheckCircle } from 'lucide-react';

export default function ShipToCard({ validationResult, selectedShipTo, selectedCustomer, shipToDropdownOpen, onShipToDropdownToggle, onSelectShipTo, onOpenCustomerShipToSearch }) {
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <h3 className="text-xs text-slate-500 tracking-wider mb-3 flex items-center gap-2">
        <Truck className="w-3 h-3" />
        Ship To
        {validationResult?.shipto_validations && !selectedShipTo?.manuallySelected && (
          <span className={`text-xs ${
            validationResult.shipto_validations.shipto_confidence === 'high' ? 'text-emerald-400' :
            validationResult.shipto_validations.shipto_confidence === 'medium' ? 'text-amber-400' : 'text-red-400'
          }`}>
            {validationResult.shipto_validations.shipto_confidence_icon} {Math.round(validationResult.shipto_validations.shipto_confidence_score * 100)}% match
          </span>
        )}
        <button
          onClick={() => onOpenCustomerShipToSearch('shipto')}
          className="ml-auto text-xs text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1"
        >
          <Search className="w-3 h-3" />
          Search
        </button>
      </h3>

      <div className="relative">
        {(selectedShipTo?.confidence ?? 1) >= 0.8 || selectedShipTo?.manuallySelected ? (
          // High confidence or accepted - show static card with green checkmark
          <div className="p-3 bg-slate-700 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white font-medium">#{selectedShipTo?.number} - {selectedShipTo?.name}</div>
                <div className="text-sm text-slate-400">{selectedShipTo?.address}</div>
                {selectedShipTo?.address2 && <div className="text-sm text-slate-400">{selectedShipTo.address2}</div>}
                <div className="text-sm text-slate-400">
                  {selectedShipTo?.city}, {selectedShipTo?.state} {selectedShipTo?.zip}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onOpenCustomerShipToSearch('shipto')}
                  className="text-xs text-slate-400 hover:text-amber-400 transition-colors flex items-center gap-1"
                >
                  <Search className="w-3 h-3" />
                  Change
                </button>
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </div>
        ) : (
          // Low/medium confidence - show dropdown button
          <>
            <button
              onClick={onShipToDropdownToggle}
              className={`w-full text-left p-3 rounded-lg flex items-start justify-between transition-colors ${
                (selectedShipTo?.confidence ?? 0) >= 0.6
                  ? 'bg-amber-500/10 border border-amber-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}
            >
              <div>
                <div className="text-white font-medium">#{selectedShipTo?.number} - {selectedShipTo?.name}</div>
                <div className="text-sm text-slate-400">{selectedShipTo?.address}</div>
                {selectedShipTo?.address2 && <div className="text-sm text-slate-400">{selectedShipTo.address2}</div>}
                <div className="text-sm text-slate-400">
                  {selectedShipTo?.city}, {selectedShipTo?.state} {selectedShipTo?.zip}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${shipToDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {shipToDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 rounded-lg shadow-xl z-10 max-h-64 overflow-auto">
                {validationResult?.shipto_validations?.shipto_alternatives?.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-xs text-slate-400 border-b border-slate-600 sticky top-0 bg-slate-700">
                      Alternative Ship-To Addresses
                    </div>
                    {validationResult.shipto_validations.shipto_alternatives.map((alt, idx) => (
                      <button
                        key={idx}
                        onClick={() => onSelectShipTo({
                          name: alt.shipto_name,
                          number: alt.shipto_number,
                          address: alt.shipto_address,
                          address2: alt.shipto_address2,
                          city: alt.shipto_city,
                          state: alt.shipto_state,
                          zip: alt.shipto_zip,
                          customerName: alt.shipto_customer_name,
                          customerNumber: alt.shipto_customer_number,
                          confidence: alt.confidence,
                          manuallySelected: true
                        })}
                        className="w-full text-left px-3 py-2 hover:bg-slate-600 transition-colors border-b border-slate-600/50 last:border-0"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-white text-sm">#{alt.shipto_number} - {alt.shipto_name}</div>
                            <div className="text-xs text-slate-400">{alt.shipto_address}</div>
                            {alt.shipto_address2 && <div className="text-xs text-slate-400">{alt.shipto_address2}</div>}
                            <div className="text-xs text-slate-400">
                              {alt.shipto_city}, {alt.shipto_state} {alt.shipto_zip}
                            </div>
                          </div>
                          <span className="text-xs text-slate-500">{Math.round(alt.confidence * 100)}%</span>
                        </div>
                      </button>
                    ))}
                  </>
                )}
                <div className="border-t border-slate-600">
                  <button
                    onClick={() => onSelectShipTo({
                      ...selectedShipTo,
                      customerName: selectedCustomer?.name,
                      manuallySelected: true
                    })}
                    className="w-full text-left px-3 py-2 text-emerald-400 hover:bg-slate-600 transition-colors text-sm flex items-center gap-2"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    Accept Current Match
                  </button>
                  <button
                    onClick={() => onOpenCustomerShipToSearch('shipto')}
                    className="w-full text-left px-3 py-2 text-amber-400 hover:bg-slate-600 transition-colors text-sm flex items-center gap-2 border-t border-slate-600/50"
                  >
                    <Search className="w-3.5 h-3.5" />
                    Search A+ Ship-To
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
